import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChatConfigResponse } from '@/api/ApiClient';

interface CurrentConfigTabProps {
  currentConfig: ChatConfigResponse | undefined;
  onEdit: (config: ChatConfigResponse) => void;
  onTest: (config: ChatConfigResponse) => void;
  onResetToEnv: () => void;
}

export default function CurrentConfigTab({
  currentConfig,
  onEdit,
  onTest,
  onResetToEnv,
}: CurrentConfigTabProps) {
  const { t } = useTranslation();

  if (!currentConfig) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('chatConfig.messages.noCurrentConfig')}
        </Typography>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {currentConfig.display_name || t('chatConfig.tabs.currentConfiguration')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {currentConfig.is_default && (
              <Chip 
                label={t('chatConfig.currentConfig.default')} 
                color="primary" 
                icon={<StarIcon />}
                size="small"
              />
            )}
            <Chip 
              label={t('chatConfig.currentConfig.active')} 
              color="success" 
              icon={<CheckIcon />}
              size="small"
            />
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chatConfig.currentConfig.provider')}
            </Typography>
            <Typography variant="body1">{currentConfig.provider_type}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chatConfig.currentConfig.model')}
            </Typography>
            <Typography variant="body1">{currentConfig.model}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chatConfig.currentConfig.temperature')}
            </Typography>
            <Typography variant="body1">{currentConfig.temperature}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chatConfig.currentConfig.maxTokens')}
            </Typography>
            <Typography variant="body1">{currentConfig.max_tokens}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chatConfig.currentConfig.streaming')}
            </Typography>
            <Typography variant="body1">
              {currentConfig.streaming ? 'Yes' : 'No'}
            </Typography>
          </Grid>
        </Grid>
        
        {currentConfig.description && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chatConfig.currentConfig.description')}
            </Typography>
            <Typography variant="body1">{currentConfig.description}</Typography>
          </Box>
        )}

        {currentConfig.system_message && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chatConfig.currentConfig.systemMessage')}
            </Typography>
            <Typography variant="body1" sx={{ 
              backgroundColor: 'grey.100', 
              p: 2, 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }}>
              {currentConfig.system_message}
            </Typography>
          </Box>
        )}
      </CardContent>
      <CardActions>
        {/* <Button
          startIcon={<EditIcon />}
          onClick={() => onEdit(currentConfig)}
        >
          {t('chatConfig.actions.edit')}
        </Button> */}
        {/* <Button
          startIcon={<TestIcon />}
          onClick={() => onTest(currentConfig)}
        >
          {t('chatConfig.actions.test')}
        </Button> */}
        {/* <Button
          startIcon={<RefreshIcon />}
          onClick={onResetToEnv}
          color="warning"
        >
          {t('chatConfig.actions.resetToEnvironment')}
        </Button> */}
      </CardActions>
    </Card>
  );
}